import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Edit, Trash2, Upload, Image, Code, Monitor, Shield, Globe, Laptop, Play, Database, Server, Smartphone, Palette, Settings, Users, BookOpen, Layers, Zap, Brain, ChevronUp, ChevronDown, DollarSign } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

type Faculty = {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string;
  order_index: number;
  is_active: boolean;
  faculty_image_url: string | null;
  created_at: string;
  updated_at: string;
};

type Course = {
  id: string;
  faculty_id: string;
  name: string;
  description: string | null;
  curriculum: string | null;
  curriculum_phases: any;
  image_url: string | null;
  background_image_url: string | null;
  individual_price: number | null;
  group_price: number | null;
  duration_months: number | null;
  weekly_frequency: number | null;
  start_date: string | null;
  end_date: string | null;
  admission_deadline: string | null;
  format: string[] | null;
  learning_type: string[] | null;
  languages: string[] | null;
  what_awaits_you: string | null;
  instructors: string[] | null;
  status: string;
  discount_percentage: number | null;
  order_index: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export const FacultyManagementSection = () => {
  const [editingFaculty, setEditingFaculty] = useState<Faculty | null>(null);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [selectedFacultyId, setSelectedFacultyId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCourseDialogOpen, setIsCourseDialogOpen] = useState(false);
  const [curriculumPhases, setCurriculumPhases] = useState<Array<{
    name: string;
    topics: string[];
  }>>([]);
  const [useImageUpload, setUseImageUpload] = useState(false);
  const [useFacultyImageUpload, setUseFacultyImageUpload] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState<string>('');
  const [colorPreview, setColorPreview] = useState<string>('#8E44AD');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [currentStatus, setCurrentStatus] = useState<string>('none');
  const [selectedCourseIds, setSelectedCourseIds] = useState<Set<string>>(new Set());
  const [isBulkPriceDialogOpen, setIsBulkPriceDialogOpen] = useState(false);
  const [bulkGroupPrice, setBulkGroupPrice] = useState('');
  const [bulkIndividualPrice, setBulkIndividualPrice] = useState('');


  // Sync currentStatus with editingCourse changes
  useEffect(() => {
    if (editingCourse) {
      setCurrentStatus(editingCourse.status || 'none');
    } else {
      setCurrentStatus('none');
    }
  }, [editingCourse]);

  // Available icons
  const availableIcons = [{
    name: 'Code',
    icon: Code,
    value: 'Code'
  }, {
    name: 'Monitor',
    icon: Monitor,
    value: 'Monitor'
  }, {
    name: 'Shield',
    icon: Shield,
    value: 'Shield'
  }, {
    name: 'Globe',
    icon: Globe,
    value: 'Globe'
  }, {
    name: 'Laptop',
    icon: Laptop,
    value: 'Laptop'
  }, {
    name: 'Play',
    icon: Play,
    value: 'Play'
  }, {
    name: 'Database',
    icon: Database,
    value: 'Database'
  }, {
    name: 'Server',
    icon: Server,
    value: 'Server'
  }, {
    name: 'Smartphone',
    icon: Smartphone,
    value: 'Smartphone'
  }, {
    name: 'Palette',
    icon: Palette,
    value: 'Palette'
  }, {
    name: 'Settings',
    icon: Settings,
    value: 'Settings'
  }, {
    name: 'Users',
    icon: Users,
    value: 'Users'
  }, {
    name: 'BookOpen',
    icon: BookOpen,
    value: 'BookOpen'
  }, {
    name: 'Layers',
    icon: Layers,
    value: 'Layers'
  }, {
    name: 'Zap',
    icon: Zap,
    value: 'Zap'
  }, {
    name: 'Brain',
    icon: Brain,
    value: 'Brain'
  }];

  const getIcon = (iconName: string | null) => {
    if (!iconName) return null;
    const IconComponent = (LucideIcons as any)[iconName];
    return IconComponent ? IconComponent : null;
  };

  const facultyImageInputRef = useRef<HTMLInputElement>(null);
  const courseImageInputRef = useRef<HTMLInputElement>(null);
  const backgroundImageInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Fetch faculties
  const { data: faculties = [] } = useQuery({
    queryKey: ['admin-faculties'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('faculties')
        .select('*')
        .order('order_index', { ascending: true });
      if (error) throw error;
      return data as Faculty[];
    }
  });

  // Fetch courses for selected faculty
  const { data: courses = [] } = useQuery({
    queryKey: ['admin-courses', selectedFacultyId],
    queryFn: async () => {
      if (!selectedFacultyId) return [];
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('faculty_id', selectedFacultyId)
        .order('order_index', { ascending: true });
      if (error) throw error;
      return data as Course[];
    },
    enabled: !!selectedFacultyId
  });

  // File upload function
  const uploadFile = async (file: File, bucket: string, folder: string) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const filePath = `${folder}/${fileName}`;
    
    const { error: uploadError, data } = await supabase.storage
      .from(bucket)
      .upload(filePath, file);
    
    if (uploadError) {
      throw uploadError;
    }
    
    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath);
    
    return publicUrl;
  };

  // Faculty mutations
  const createFacultyMutation = useMutation({
    mutationFn: async (facultyData: Omit<Faculty, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('faculties')
        .insert([facultyData])
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-faculties'] });
      toast.success("Ֆակուլտետը հաջողությամբ ստեղծվել է");
      setIsDialogOpen(false);
      setEditingFaculty(null);
      setUseFacultyImageUpload(false);
      setColorPreview('#8E44AD');
      setImagePreview(null);
      setSelectedIcon('');
    },
    onError: () => {
      toast.error("Սխալ ֆակուլտետ ստեղծելիս");
    }
  });

  const updateFacultyMutation = useMutation({
    mutationFn: async ({ id, ...facultyData }: Partial<Faculty> & { id: string }) => {
      const { data, error } = await supabase
        .from('faculties')
        .update(facultyData)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-faculties'] });
      toast.success("Ֆակուլտետը հաջողությամբ թարմացվել է");
      setIsDialogOpen(false);
      setEditingFaculty(null);
      setUseFacultyImageUpload(false);
      setColorPreview('#8E44AD');
      setImagePreview(null);
      setSelectedIcon('');
    },
    onError: () => {
      toast.error("Սխալ ֆակուլտետ թարմացնելիս");
    }
  });

  const deleteFacultyMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('faculties')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-faculties'] });
      toast.success("Ֆակուլտետը հաջողությամբ ջնջվել է");
    },
    onError: () => {
      toast.error("Սխալ ֆակուլտետ ջնջելիս");
    }
  });

  // Course mutations
  const createCourseMutation = useMutation({
    mutationFn: async (courseData: Omit<Course, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('courses')
        .insert([courseData])
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-courses', selectedFacultyId] });
      queryClient.invalidateQueries({ queryKey: ['faculties-with-courses'] });
      toast.success("Դասընթացը հաջողությամբ ստեղծվել է");
      setIsCourseDialogOpen(false);
      setEditingCourse(null);
      setCurriculumPhases([]);
      setUseImageUpload(false);
      setCurrentStatus('none');
    },
    onError: () => {
      toast.error("Սխալ դասընթաց ստեղծելիս");
    }
  });

  const updateCourseMutation = useMutation({
    mutationFn: async ({ id, ...courseData }: Partial<Course> & { id: string }) => {
      const { data, error } = await supabase
        .from('courses')
        .update(courseData)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-courses', selectedFacultyId] });
      queryClient.invalidateQueries({ queryKey: ['faculties-with-courses'] });
      toast.success("Դասընթացը հաջողությամբ թարմացվել է");
      setIsCourseDialogOpen(false);
      setEditingCourse(null);
      setCurriculumPhases([]);
      setUseImageUpload(false);
      setCurrentStatus('none');
    },
    onError: () => {
      toast.error("Սխալ դասընթաց թարմացնելիս");
    }
  });

  const deleteCourseMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('courses')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-courses', selectedFacultyId] });
      queryClient.invalidateQueries({ queryKey: ['faculties-with-courses'] });
      toast.success("Դասընթացը հաջողությամբ ջնջվել է");
    },
    onError: () => {
      toast.error("Սխալ դասընթաց ջնջելիս");
    }
  });

  // Bulk price update mutation
  const bulkUpdatePricesMutation = useMutation({
    mutationFn: async ({ courseIds, groupPrice, individualPrice }: { courseIds: string[]; groupPrice?: number; individualPrice?: number }) => {
      const updateData: Record<string, number> = {};
      if (groupPrice !== undefined) updateData.group_price = groupPrice;
      if (individualPrice !== undefined) updateData.individual_price = individualPrice;
      
      const { error } = await supabase
        .from('courses')
        .update(updateData)
        .in('id', courseIds);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-courses', selectedFacultyId] });
      queryClient.invalidateQueries({ queryKey: ['faculties-with-courses'] });
      toast.success('\u0533\u0576\u0565\u0580\u0568 \u0570\u0561\u057b\u0578\u0572\u0578\u0582\u0569\u0575\u0561\u0574\u0562 \u0569\u0561\u0580\u0574\u0561\u0581\u057e\u0565\u0581\u056b\u0576');
      setIsBulkPriceDialogOpen(false);
      setSelectedCourseIds(new Set());
      setBulkGroupPrice('');
      setBulkIndividualPrice('');
    },
    onError: () => {
      toast.error('\u054d\u056d\u0561\u056c \u0563\u0576\u0565\u0580\u0568 \u0569\u0561\u0580\u0574\u0561\u0581\u0576\u0565\u056c\u056b\u057d');
    }
  });

  const toggleCourseSelection = (courseId: string) => {
    setSelectedCourseIds(prev => {
      const next = new Set(prev);
      if (next.has(courseId)) next.delete(courseId);
      else next.add(courseId);
      return next;
    });
  };

  const toggleAllCourses = () => {
    if (selectedCourseIds.size === courses.length) {
      setSelectedCourseIds(new Set());
    } else {
      setSelectedCourseIds(new Set(courses.map(c => c.id)));
    }
  };

  const handleBulkPriceSubmit = () => {
    if (selectedCourseIds.size === 0) return;
    const gp = bulkGroupPrice ? parseInt(bulkGroupPrice) : undefined;
    const ip = bulkIndividualPrice ? parseInt(bulkIndividualPrice) : undefined;
    if (!gp && !ip) {
      toast.error('\u0546\u0577\u0565\u0584 \u0561\u057c\u0576\u057e\u0561\u0566\u0576 \u0574\u0565\u056f \u0563\u056b\u0576');
      return;
    }
    bulkUpdatePricesMutation.mutate({ courseIds: Array.from(selectedCourseIds), groupPrice: gp, individualPrice: ip });
  };

  const handleFacultySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const name = formData.get('name') as string;
    if (!name || name.trim().length < 2) {
      toast.error("Ֆակուլտետի անվանումը պետք է լինի առնվազն 2 տառ");
      return;
    }

    const duplicateFaculty = faculties.find(f => 
      f.name.toLowerCase() === name.toLowerCase() && f.id !== editingFaculty?.id
    );
    if (duplicateFaculty) {
      toast.error("Այս անվանմամբ ֆակուլտետն արդեն գոյություն ունի");
      return;
    }

    let facultyImageUrl = editingFaculty?.faculty_image_url || null;

    if (useFacultyImageUpload && facultyImageInputRef.current?.files?.[0]) {
      const file = facultyImageInputRef.current.files[0];

      if (file.size > 5 * 1024 * 1024) {
        toast.error("Նկարի չափը չպետք է գերազանցի 5ՄԲ");
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast.error("Ընտրեք միայն նկարային ֆայլ");
        return;
      }

      try {
        toast("Նկարը վերբեռնվում է...", { duration: 2000 });
        facultyImageUrl = await uploadFile(file, 'course-images', 'faculties');
        toast.success("Նկարը հաջողությամբ վերբեռնվել է");
      } catch (error) {
        console.error('Faculty image upload error:', error);
        toast.error("Նկարի վերբեռնման սխալ");
        return;
      }
    }

    const facultyData = {
      name: name.trim(),
      description: (formData.get('description') as string)?.trim() || null,
      icon: !useFacultyImageUpload ? (formData.get('icon') as string)?.trim() || null : null,
      faculty_image_url: facultyImageUrl,
      color: formData.get('color') as string,
      order_index: parseInt(formData.get('order_index') as string) || 0,
      is_active: formData.get('is_active') === 'true'
    };

    if (editingFaculty) {
      updateFacultyMutation.mutate({ id: editingFaculty.id, ...facultyData });
    } else {
      createFacultyMutation.mutate(facultyData);
    }
  };

  const handleCourseSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const name = formData.get('name') as string;
    if (!name || name.trim().length < 3) {
      toast.error("Դասընթացի անվանումը պետք է լինի առնվազն 3 տառ");
      return;
    }

    const duplicateCourse = courses.find(c => 
      c.name.toLowerCase() === name.toLowerCase() && c.id !== editingCourse?.id
    );
    if (duplicateCourse) {
      toast.error("Այս անվանմամբ դասընթացն արդեն գոյություն ունի այս ֆակուլտետում");
      return;
    }

    if (curriculumPhases.length === 0 && !formData.get('curriculum')) {
      toast.error("Ավելացրեք ուսումնական ծրագիր կամ ֆազեր");
      return;
    }

    if (curriculumPhases.length > 0) {
      for (let i = 0; i < curriculumPhases.length; i++) {
        const phase = curriculumPhases[i];
        if (!phase.name || phase.name.trim().length < 2) {
          toast.error(`Փուլ ${i + 1}-ի անվանումը չի կարող դատարկ լինել`);
          return;
        }
        if (!phase.topics || phase.topics.length === 0 || phase.topics.every(t => !t.trim())) {
          toast.error(`Փուլ ${i + 1}-ում առնվազն մեկ թեմա պետք է լինի`);
          return;
        }
      }
    }

    const individualPrice = parseInt(formData.get('individual_price') as string);
    const groupPrice = parseInt(formData.get('group_price') as string);
    if (!individualPrice && !groupPrice) {
      toast.error("Նշեք առնվազն մեկ գին (անհատական կամ խմբային)");
      return;
    }

    const startDate = formData.get('start_date') as string;
    const endDate = formData.get('end_date') as string;
    const admissionDeadline = formData.get('admission_deadline') as string;
    
    if (startDate && endDate && new Date(startDate) >= new Date(endDate)) {
      toast.error("Ավարտի ամսաթիվը պետք է ավելի ուշ լինի սկզբի ամսաթվից");
      return;
    }
    if (admissionDeadline && startDate && new Date(admissionDeadline) >= new Date(startDate)) {
      toast.error("Ընդունելության ժամկետը պետք է սկզբի ամսաթվից առաջ լինի");
      return;
    }

    let imageUrl = editingCourse?.image_url || null;
    let backgroundImageUrl = editingCourse?.background_image_url || null;

    if (useImageUpload && courseImageInputRef.current?.files?.[0]) {
      const file = courseImageInputRef.current.files[0];
      try {
        toast("Նկարը վերբեռնվում է...", { duration: 2000 });
        imageUrl = await uploadFile(file, 'course-images', 'courses');
        toast.success("Նկարը հաջողությամբ վերբեռնվել է");
      } catch (error) {
        console.error('Course image upload error:', error);
        toast.error("Նկարի վերբեռնման սխալ");
        return;
      }
    } else if (!useImageUpload) {
      imageUrl = (formData.get('image_url') as string) || null;
    }

    if (backgroundImageInputRef.current?.files?.[0]) {
      const file = backgroundImageInputRef.current.files[0];
      try {
        backgroundImageUrl = await uploadFile(file, 'course-images', 'backgrounds');
      } catch (error) {
        console.error('Background image upload error:', error);
        toast.error("Հետնանկարի վերբեռնման սխալ");
        return;
      }
    }

    const formatArray = Array.from(formData.getAll('format')) as string[];
    const learningTypeArray = Array.from(formData.getAll('learning_type')) as string[];
    const languagesArray = Array.from(formData.getAll('languages')) as string[];
    const instructorsText = formData.get('instructors') as string;
    const instructorsArray = instructorsText 
      ? instructorsText.split('\n').map(i => i.trim()).filter(i => i.length > 0)
      : null;

    const curriculumPhasesData = curriculumPhases.length > 0 
      ? curriculumPhases.map((phase, index) => ({
          phase_number: index + 1,
          name: phase.name,
          topics: phase.topics.filter(topic => topic.trim().length > 0)
        }))
      : null;

    const courseData = {
      faculty_id: selectedFacultyId!,
      name: name.trim(),
      description: (formData.get('description') as string)?.trim() || null,
      curriculum: curriculumPhases.length === 0 ? (formData.get('curriculum') as string)?.trim() || null : null,
      curriculum_phases: curriculumPhasesData,
      image_url: imageUrl,
      background_image_url: backgroundImageUrl,
      individual_price: individualPrice || 90000,
      group_price: groupPrice || 60000,
      duration_months: parseInt(formData.get('duration_months') as string) || 6,
      weekly_frequency: parseInt(formData.get('weekly_frequency') as string) || 3,
      start_date: startDate || '2025-08-15',
      end_date: endDate || null,
      admission_deadline: admissionDeadline || null,
      format: formatArray.length > 0 ? formatArray : ['online', 'in_person', 'hybrid'],
      learning_type: learningTypeArray.length > 0 ? learningTypeArray : ['group', 'individual'],
      languages: languagesArray.length > 0 ? languagesArray : ['հայերեն', 'ռուսերեն', 'անգլերեն'],
      what_awaits_you: (formData.get('what_awaits_you') as string)?.trim() || null,
      instructors: instructorsArray,
      status: currentStatus,
      discount_percentage: currentStatus === 'discounted' 
        ? parseInt(formData.get('discount_percentage') as string) || null 
        : null,
      order_index: parseInt(formData.get('order_index') as string) || 0,
      is_active: formData.get('is_active') === 'true'
    };

    if (editingCourse) {
      updateCourseMutation.mutate({ id: editingCourse.id, ...courseData });
    } else {
      createCourseMutation.mutate(courseData);
    }
  };

  // Curriculum management functions
  const addCurriculumPhase = () => {
    setCurriculumPhases([...curriculumPhases, { name: '', topics: [''] }]);
  };

  const updatePhase = (index: number, field: 'name', value: string) => {
    const updated = [...curriculumPhases];
    updated[index] = { ...updated[index], [field]: value };
    setCurriculumPhases(updated);
  };

  const addTopic = (phaseIndex: number) => {
    const updated = [...curriculumPhases];
    updated[phaseIndex].topics = [...updated[phaseIndex].topics, ''];
    setCurriculumPhases(updated);
  };

  const updateTopic = (phaseIndex: number, topicIndex: number, value: string) => {
    const updated = [...curriculumPhases];
    updated[phaseIndex].topics[topicIndex] = value;
    setCurriculumPhases(updated);
  };

  const removeTopic = (phaseIndex: number, topicIndex: number) => {
    const updated = [...curriculumPhases];
    updated[phaseIndex].topics.splice(topicIndex, 1);
    setCurriculumPhases(updated);
  };

  const removePhase = (index: number) => {
    const updated = [...curriculumPhases];
    updated.splice(index, 1);
    setCurriculumPhases(updated);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold text-primary">Ֆակուլտետների և Դասընթացների Կառավարում</h2>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            className="border-border/70 text-primary hover:bg-primary/10"
            onClick={async () => {
              if (confirm('Վստա՞հ եք, որ ցանկանում եք ստանդարտացնել բոլոր դասընթացների պարամետրերը:')) {
                try {
                  await supabase.from('courses').update({
                    individual_price: 90000,
                    group_price: 60000,
                    weekly_frequency: 3,
                    start_date: '2025-08-15',
                    format: ['online', 'in_person', 'hybrid'],
                    learning_type: ['group', 'individual'],
                    languages: ['հայերեն', 'ռուսերեն', 'անգլերեն']
                  }).neq('id', '00000000-0000-0000-0000-000000000000');
                  
                  queryClient.invalidateQueries({ queryKey: ['admin-courses'] });
                  queryClient.invalidateQueries({ queryKey: ['faculties-with-courses'] });
                  toast.success('Բոլոր դասընթացները ստանդարտացվել են');
                } catch (error) {
                  toast.error('Սխալ ստանդարտացման ժամանակ');
                }
              }
            }}
          >
            Ստանդարտացնել
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => {
                setEditingFaculty(null);
                setUseFacultyImageUpload(false);
                setSelectedIcon('');
              }} className="bg-primary hover:bg-primary/90">
                <Plus className="h-4 w-4 mr-2" />
                Նոր Ֆակուլտետ
              </Button>
            </DialogTrigger>
            <DialogContent className="border-border/70 bg-card/80 max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-primary">
                  {editingFaculty ? 'Խմբագրել Ֆակուլտետը' : 'Ստեղծել Նոր Ֆակուլտետ'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleFacultySubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-primary">Անվանում</Label>
                    <Input 
                      id="name" 
                      name="name" 
                      required 
                      defaultValue={editingFaculty?.name || ''} 
                      className="bg-muted border-border/70" 
                    />
                  </div>
                  <div>
                    <Label htmlFor="description" className="text-primary">Նկարագրություն</Label>
                    <Textarea 
                      id="description" 
                      name="description" 
                      defaultValue={editingFaculty?.description || ''} 
                      className="bg-muted border-border/70" 
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-primary">Ֆակուլտետի նկար</Label>
                  <div className="flex items-center space-x-2 mt-2">
                    <Checkbox 
                      checked={useFacultyImageUpload} 
                      onCheckedChange={(checked) => setUseFacultyImageUpload(checked as boolean)} 
                    />
                    <span className="text-sm text-foreground">Նկար վերբեռնել</span>
                  </div>
                </div>
                
                {useFacultyImageUpload ? (
                  <div>
                    <Label htmlFor="faculty_image" className="text-primary">Նկար վերբեռնել</Label>
                    <Input 
                      id="faculty_image" 
                      ref={facultyImageInputRef} 
                      type="file" 
                      accept="image/*" 
                      className="bg-muted border-border/70" 
                    />
                    {imagePreview && (
                      <div className="mt-2">
                        <img src={imagePreview} alt="Preview" className="w-20 h-20 object-cover rounded" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="icon" className="text-primary">Ընտրել պատկերակ</Label>
                    <Select 
                      name="icon" 
                      defaultValue={editingFaculty?.icon || selectedIcon}
                      onValueChange={setSelectedIcon}
                    >
                      <SelectTrigger className="bg-muted border-border/70">
                        <SelectValue placeholder="Ընտրեք պատկերակ" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableIcons.map((iconOption) => {
                          const IconComponent = iconOption.icon;
                          return (
                            <SelectItem key={iconOption.value} value={iconOption.value}>
                              <div className="flex items-center space-x-2">
                                <IconComponent className="w-4 h-4" />
                                <span>{iconOption.name}</span>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="color" className="text-primary">Գույն</Label>
                    <div className="flex items-center space-x-3">
                      <Input 
                        id="color" 
                        name="color" 
                        type="color" 
                        defaultValue={editingFaculty?.color || '#8E44AD'} 
                        onChange={(e) => setColorPreview(e.target.value)} 
                        className="bg-muted border-border/70 w-20 h-10" 
                      />
                      <div 
                        className="w-10 h-10 rounded border border-border flex items-center justify-center" 
                        style={{ backgroundColor: colorPreview }}
                      >
                        <span className="text-foreground text-xs font-bold">#</span>
                      </div>
                      <span className="text-muted-foreground text-sm">{colorPreview}</span>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="order_index" className="text-primary">Դասակարգում</Label>
                    <Input 
                      id="order_index" 
                      name="order_index" 
                      type="number" 
                      defaultValue={editingFaculty?.order_index || 0} 
                      className="bg-muted border-border/70" 
                    />
                  </div>
                  <div>
                    <Label htmlFor="is_active" className="text-primary">Կարգավիճակ</Label>
                    <Select name="is_active" defaultValue={editingFaculty?.is_active ? 'true' : 'false'}>
                      <SelectTrigger className="bg-muted border-border/70">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Ակտիվ</SelectItem>
                        <SelectItem value="false">Ոչ ակտիվ</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Չեղարկել
                  </Button>
                  <Button type="submit" className="bg-primary hover:bg-primary/90">
                    {editingFaculty ? 'Թարմացնել' : 'Ստեղծել'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Faculties List */}
        <Card className="bg-card/50 border-border/70">
          <CardHeader>
            <CardTitle className="text-primary">Ֆակուլտետներ</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {faculties.map((faculty) => (
                <div 
                  key={faculty.id} 
                  className={`flex items-center justify-between p-3 rounded border cursor-pointer transition-colors ${
                    selectedFacultyId === faculty.id 
                      ? 'bg-primary/20 border-border/70' 
                      : 'bg-muted/50 border-border hover:bg-muted'
                  }`}
                  onClick={() => setSelectedFacultyId(faculty.id)}
                >
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-8 h-8 rounded flex items-center justify-center" 
                      style={{ backgroundColor: faculty.color + '20' }}
                    >
                      {faculty.faculty_image_url ? (
                        <img 
                          src={faculty.faculty_image_url} 
                          alt={faculty.name} 
                          className="w-full h-full object-cover rounded" 
                        />
                      ) : (() => {
                        const IconComponent = getIcon(faculty.icon);
                        return IconComponent ? (
                          <IconComponent 
                            className="w-5 h-5" 
                            style={{ color: faculty.color }} 
                          />
                        ) : (
                          <div className="w-5 h-5 bg-muted rounded" />
                        );
                      })()}
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground">{faculty.name}</h3>
                      <Badge variant={faculty.is_active ? "default" : "secondary"} className="text-xs">
                        {faculty.is_active ? 'Ակտիվ' : 'Ոչ ակտիվ'}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingFaculty(faculty);
                        setUseFacultyImageUpload(!!faculty.faculty_image_url);
                        setSelectedIcon(faculty.icon || '');
                        setColorPreview(faculty.color || '#8E44AD');
                        setImagePreview(faculty.faculty_image_url);
                        setIsDialogOpen(true);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-destructive hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm('Վստա՞հ եք, որ ցանկանում եք ջնջել այս ֆակուլտետը:')) {
                          deleteFacultyMutation.mutate(faculty.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Courses List */}
        <Card className="bg-card/50 border-border/70">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-primary">Դասընթացներ</CardTitle>
              {selectedFacultyId && (
                <Dialog open={isCourseDialogOpen} onOpenChange={setIsCourseDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      size="sm" 
                      className="bg-primary hover:bg-primary/90" 
                      onClick={() => {
                        setEditingCourse(null);
                        setCurriculumPhases([]);
                        setUseImageUpload(false);
                        setCurrentStatus('none');
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Նոր դասընթաց
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="border-border/70 bg-card/80 max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-primary">
                        {editingCourse ? 'Խմբագրել դասընթացը' : 'Ստեղծել նոր դասընթաց'}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCourseSubmit} className="space-y-6">
                      {/* Basic Info Section */}
                      <div className="bg-card/50 p-4 rounded-lg border border-border/70">
                        <h3 className="text-lg font-semibold text-primary mb-4">Հիմնական տեղեկություններ</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="course-name" className="text-primary font-medium">
                              Անվանում <span className="text-destructive">*</span>
                            </Label>
                            <Input 
                              id="course-name" 
                              name="name" 
                              required 
                              defaultValue={editingCourse?.name || ''} 
                              className="bg-muted border-border/70" 
                            />
                          </div>
                          <div>
                            <Label htmlFor="course-description" className="text-primary font-medium">Նկարագրություն</Label>
                            <Textarea 
                              id="course-description" 
                              name="description" 
                              defaultValue={editingCourse?.description || ''} 
                              className="bg-muted border-border/70" 
                              rows={3} 
                            />
                          </div>
                        </div>
                      </div>

                      {/* Curriculum Section */}
                      <div className="bg-card/50 p-4 rounded-lg border border-border/70">
                        <h3 className="text-lg font-semibold text-primary mb-4">Ուսումնական ծրագիր</h3>
                        <div>
                          <div className="space-y-4">
                            {curriculumPhases.map((phase, phaseIndex) => (
                              <div key={phaseIndex} className="border border-border/70 rounded-lg p-4">
                                <div className="flex justify-between items-center mb-3">
                                  <Label className="text-primary font-medium">
                                    Փուլ {phaseIndex + 1}
                                  </Label>
                                  <Button 
                                    type="button" 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => removePhase(phaseIndex)}
                                    className="text-destructive hover:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                                <Input
                                  value={phase.name}
                                  onChange={(e) => updatePhase(phaseIndex, 'name', e.target.value)}
                                  placeholder="Փուլի անվանում"
                                  className="bg-muted border-border/70 mb-3"
                                />
                                <div className="space-y-2">
                                  <Label className="text-primary text-sm">Թեմաներ:</Label>
                                  {phase.topics.map((topic, topicIndex) => (
                                    <div key={topicIndex} className="flex items-center space-x-2">
                                      <Input
                                        value={topic}
                                        onChange={(e) => updateTopic(phaseIndex, topicIndex, e.target.value)}
                                        placeholder="Թեմայի անվանում"
                                        className="bg-muted border-border/70 flex-1"
                                      />
                                      {phase.topics.length > 1 && (
                                        <Button 
                                          type="button" 
                                          variant="ghost" 
                                          size="sm" 
                                          onClick={() => removeTopic(phaseIndex, topicIndex)}
                                          className="text-destructive hover:text-destructive"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </div>
                                  ))}
                                  <Button 
                                    type="button" 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => addTopic(phaseIndex)} 
                                    className="mt-2 text-primary border-border/70 hover:bg-primary/10"
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Ավելացնել թեմա
                                  </Button>
                                </div>
                              </div>
                            ))}
                            <Button 
                              type="button" 
                              variant="outline" 
                              onClick={addCurriculumPhase} 
                              className="w-full"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Ավելացնել փուլ
                            </Button>
                          </div>
                        </div>
                        
                        {/* Fallback simple curriculum if no phases */}
                        {curriculumPhases.length === 0 && (
                          <div className="mt-4">
                            <Label htmlFor="curriculum" className="text-primary font-medium">Ուսումնական Ծրագիր (տեքստ)</Label>
                            <Textarea 
                              id="curriculum" 
                              name="curriculum" 
                              defaultValue={editingCourse?.curriculum || ''} 
                              className="bg-muted border-border/70" 
                              rows={4} 
                              placeholder="Նկարագրեք ուսումնական ծրագիրը..." 
                            />
                          </div>
                        )}
                      </div>
                     
                      {/* Pricing & Duration Section */}
                      <div className="bg-card/50 p-4 rounded-lg border border-border/70">
                        <h3 className="text-lg font-semibold text-primary mb-4">Գին և ժամկետներ</h3>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label htmlFor="individual_price" className="text-primary font-medium">
                              Անհատական Արժեք (դրամ/ամիս)
                            </Label>
                            <Input 
                              id="individual_price" 
                              name="individual_price" 
                              type="number" 
                              min="0" 
                              defaultValue={editingCourse?.individual_price || 90000} 
                              className="bg-muted border-border/70" 
                              placeholder="90000" 
                            />
                          </div>
                          <div>
                            <Label htmlFor="group_price" className="text-primary font-medium">
                              Խմբային Արժեք (դրամ/ամիս)
                            </Label>
                            <Input 
                              id="group_price" 
                              name="group_price" 
                              type="number" 
                              min="0" 
                              defaultValue={editingCourse?.group_price || 60000} 
                              className="bg-muted border-border/70" 
                              placeholder="60000" 
                            />
                          </div>
                          <div>
                            <Label htmlFor="duration_months" className="text-primary font-medium">Տևողություն</Label>
                            <Select name="duration_months" defaultValue={editingCourse?.duration_months?.toString() || '6'}>
                              <SelectTrigger className="bg-muted border-border/70">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="6">6 ամիս (Բազային)</SelectItem>
                                <SelectItem value="9">9 ամիս (Խորացված)</SelectItem>
                                <SelectItem value="18">18 ամիս (Ակադեմիական)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div>
                            <Label htmlFor="weekly_frequency" className="text-primary font-medium">Շաբաթական հաճախությունը</Label>
                            <Input 
                              id="weekly_frequency" 
                              name="weekly_frequency" 
                              type="number" 
                              min="1" 
                              max="7" 
                              defaultValue={editingCourse?.weekly_frequency || 3} 
                              className="bg-muted border-border/70" 
                              placeholder="3" 
                            />
                            <p className="text-xs text-muted-foreground mt-1">Ստանդարտ: 3 անգամ շաբաթը</p>
                          </div>
                          <div>
                            <Label htmlFor="lesson_duration" className="text-primary font-medium">Դասի տևողությունը</Label>
                            <Input 
                              id="lesson_duration" 
                              name="lesson_duration" 
                              type="text" 
                              defaultValue="1.5-2 ժամ" 
                              readOnly 
                              className="bg-muted border-border/70" 
                            />
                            <p className="text-xs text-muted-foreground mt-1">Ստանդարտ: 1.5-2 ժամ</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 mt-4">
                          <div>
                            <Label htmlFor="start_date" className="text-primary font-medium">Սկսման Ամսաթիվ</Label>
                            <Input 
                              id="start_date" 
                              name="start_date" 
                              type="date" 
                              defaultValue={editingCourse?.start_date || '2025-08-15'} 
                              className="bg-muted border-border/70" 
                            />
                            <p className="text-xs text-muted-foreground mt-1">Ստանդարտ սկիզբ: 15.08.2025</p>
                          </div>
                          <div>
                            <Label htmlFor="end_date" className="text-primary font-medium">Ավարտի Ամսաթիվ</Label>
                            <Input 
                              id="end_date" 
                              name="end_date" 
                              type="date" 
                              defaultValue={editingCourse?.end_date || ''} 
                              className="bg-muted border-border/70" 
                            />
                          </div>
                          <div>
                            <Label htmlFor="admission_deadline" className="text-primary font-medium">Ընդունելության ժամկետ</Label>
                            <Input 
                              id="admission_deadline" 
                              name="admission_deadline" 
                              type="date" 
                              defaultValue={editingCourse?.admission_deadline || ''} 
                              className="bg-muted border-border/70" 
                            />
                          </div>
                        </div>
                      </div>
                     
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-primary">Ֆորմատ</Label>
                          <div className="flex flex-col space-y-2 mt-2">
                            {['online', 'in_person', 'hybrid'].map((formatOption) => (
                              <label key={formatOption} className="flex items-center space-x-2">
                                <Checkbox 
                                  name="format" 
                                  value={formatOption} 
                                  defaultChecked={editingCourse?.format?.includes(formatOption) || ['online', 'in_person', 'hybrid'].includes(formatOption)} 
                                />
                                <span className="text-foreground">
                                  {formatOption === 'online' && 'Օնլայն'}
                                  {formatOption === 'in_person' && 'Առկա'}
                                  {formatOption === 'hybrid' && 'Հիբրիդային'}
                                </span>
                              </label>
                            ))}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Ստանդարտ: բոլոր ֆորմատները</p>
                        </div>
                        <div>
                          <Label className="text-primary">Ուսումնական տեսակ</Label>
                          <div className="flex flex-col space-y-2 mt-2">
                            {['group', 'individual'].map((learningOption) => (
                              <label key={learningOption} className="flex items-center space-x-2">
                                <Checkbox 
                                  name="learning_type" 
                                  value={learningOption} 
                                  defaultChecked={editingCourse?.learning_type?.includes(learningOption) || ['group', 'individual'].includes(learningOption)} 
                                />
                                <span className="text-foreground">
                                  {learningOption === 'group' && 'Խմբային'}
                                  {learningOption === 'individual' && 'Անհատական'}
                                </span>
                              </label>
                            ))}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Ստանդարտ: բոլոր տեսակները</p>
                        </div>
                      </div>
                       
                      <div>
                        <Label className="text-primary">Լեզուներ</Label>
                        <div className="flex space-x-4 mt-2">
                          {['հայերեն', 'ռուսերեն', 'անգլերեն'].map((language) => (
                            <label key={language} className="flex items-center space-x-2">
                              <Checkbox 
                                name="languages" 
                                value={language} 
                                defaultChecked={editingCourse?.languages?.includes(language) || ['հայերեն', 'ռուսերեն', 'անգլերեն'].includes(language)} 
                              />
                              <span className="text-foreground">{language}</span>
                            </label>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Ստանդարտ: բոլոր լեզուները</p>
                      </div>
                      
                      <div>
                        <Label htmlFor="what_awaits_you" className="text-primary">Ինչ է ձեզ սպասավում</Label>
                        <Textarea 
                          id="what_awaits_you" 
                          name="what_awaits_you" 
                          defaultValue={editingCourse?.what_awaits_you || ''} 
                          className="bg-muted border-border/70" 
                          rows={3} 
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="instructors" className="text-primary">Դասախոսներ (յուրաքանչյուրը նոր տողում)</Label>
                        <Textarea 
                          id="instructors" 
                          name="instructors" 
                          defaultValue={editingCourse?.instructors?.join('\n') || ''} 
                          className="bg-muted border-border/70" 
                          rows={3} 
                          placeholder="Դասախոս 1&#10;Դասախոս 2" 
                        />
                      </div>
                      
                      {/* Image Upload Section */}
                      <div className="space-y-4">
                        <div>
                          <Label className="text-primary">Դասընթացի նկար</Label>
                          <div className="flex items-center space-x-2 mt-2">
                            <Checkbox 
                              checked={useImageUpload} 
                              onCheckedChange={(checked) => setUseImageUpload(checked as boolean)} 
                            />
                            <span className="text-sm text-foreground">Նկար վերբեռնել</span>
                          </div>
                        </div>
                        
                        {useImageUpload ? (
                          <div>
                            <Label htmlFor="course_image" className="text-primary">Նկար վերբեռնել</Label>
                            <Input 
                              id="course_image" 
                              ref={courseImageInputRef} 
                              type="file" 
                              accept="image/*" 
                              className="bg-muted border-border/70" 
                            />
                          </div>
                        ) : (
                          <div>
                            <Label htmlFor="image_url" className="text-primary">Նկարի URL</Label>
                            <Input 
                              id="image_url" 
                              name="image_url" 
                              defaultValue={editingCourse?.image_url || ''} 
                              className="bg-muted border-border/70" 
                            />
                          </div>
                        )}
                        
                        <div>
                          <Label htmlFor="background_image" className="text-primary">Լենդինգ էջի հետնանկար</Label>
                          <Input 
                            id="background_image" 
                            ref={backgroundImageInputRef} 
                            type="file" 
                            accept="image/*" 
                            className="bg-muted border-border/70" 
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="course_order_index" className="text-primary">Դասակարգում</Label>
                          <Input 
                            id="course_order_index" 
                            name="order_index" 
                            type="number" 
                            defaultValue={editingCourse?.order_index || 0} 
                            className="bg-muted border-border/70" 
                          />
                        </div>
                        <div>
                          <Label htmlFor="course_is_active" className="text-primary">Կարգավիճակ</Label>
                          <Select name="is_active" defaultValue={editingCourse?.is_active ? 'true' : 'false'}>
                            <SelectTrigger className="bg-muted border-border/70">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="true">Ակտիվ</SelectItem>
                              <SelectItem value="false">Ոչ ակտիվ</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="flex justify-end space-x-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setIsCourseDialogOpen(false)}>
                          Չեղարկել
                        </Button>
                        <Button type="submit" className="bg-primary hover:bg-primary/90">
                          {editingCourse ? 'Թարմացնել' : 'Ստեղծել'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {selectedFacultyId ? (
              <div className="space-y-3">
                {/* Bulk actions bar */}
                {courses.length > 0 && (
                  <div className="flex items-center justify-between p-3 rounded border bg-card/50 border-border/70">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        checked={courses.length > 0 && selectedCourseIds.size === courses.length}
                        onCheckedChange={toggleAllCourses}
                      />
                      <span className="text-sm text-muted-foreground">
                        {selectedCourseIds.size > 0
                          ? `Ընտրված է ${selectedCourseIds.size} դասընթաց`
                          : 'Ընտրել բոլորը'}
                      </span>
                    </div>
                    {selectedCourseIds.size > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-border/70 text-primary hover:bg-primary/10"
                        onClick={() => setIsBulkPriceDialogOpen(true)}
                      >
                        <DollarSign className="h-4 w-4 mr-1" />
                        Փոխել գները ({selectedCourseIds.size})
                      </Button>
                    )}
                  </div>
                )}

                {courses.map((course) => (
                  <div 
                    key={course.id} 
                    className="flex items-center justify-between p-3 rounded border bg-muted/50 border-border"
                  >
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        checked={selectedCourseIds.has(course.id)}
                        onCheckedChange={() => toggleCourseSelection(course.id)}
                      />
                      <div>
                        <h4 className="font-medium text-foreground">{course.name}</h4>
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge variant={course.is_active ? "default" : "secondary"} className="text-xs">
                            {course.is_active ? '\u0531\u056F\u057F\u056B\u057E' : '\u0548\u0579 \u0561\u056F\u057F\u056B\u057E'}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {course.duration_months ? `${course.duration_months} \u0561\u0574\u056B\u057D` : '\u054F\u0587\u0578\u0572\u0578\u0582\u0569\u0575\u0578\u0582\u0576 \u0576\u0577\u057E\u0561\u056E \u0579\u0567'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {'\u0531\u0576\u0570\u0561\u057F\u0561\u056F\u0561\u0576'}: {course.individual_price || 90000} {'\u0564\u0580/\u0561\u0574\u056B\u057D'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {'\u053D\u0574\u0562\u0561\u0575\u056B\u0576'}: {course.group_price || 60000} {'\u0564\u0580/\u0561\u0574\u056B\u057D'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => {
                          setEditingCourse(course);
                          if (course.curriculum_phases && Array.isArray(course.curriculum_phases)) {
                            setCurriculumPhases(course.curriculum_phases.map((phase: any) => ({
                              name: phase.name || '',
                              topics: phase.topics || ['']
                            })));
                          } else {
                            setCurriculumPhases([]);
                          }
                          setUseImageUpload(false);
                          setCurrentStatus(course.status || 'none');
                          setIsCourseDialogOpen(true);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          if (confirm('\u054E\u057D\u057F\u0561\u0301\u0570 \u0565\u0584, \u0578\u0580 \u0581\u0561\u0576\u056F\u0561\u0576\u0578\u0582\u0574 \u0565\u0584 \u057B\u0576\u057B\u0565\u056C \u0561\u0575\u057D \u0564\u0561\u057D\u0568\u0576\u0569\u0561\u0581\u0568:')) {
                            deleteCourseMutation.mutate(course.id);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {courses.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">
                    {'\u0531\u0575\u057D \u0586\u0561\u056F\u0578\u0582\u056C\u057F\u0565\u057F\u0578\u0582\u0574 \u0564\u0565\u057C \u0564\u0561\u057D\u0568\u0576\u0569\u0561\u0581\u0576\u0565\u0580 \u0579\u056F\u0561\u0576:'}
                  </p>
                )}

                {/* Bulk Price Dialog */}
                <Dialog open={isBulkPriceDialogOpen} onOpenChange={setIsBulkPriceDialogOpen}>
                  <DialogContent className="border-border/70 bg-card/80 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-primary">
                        {'\u0553\u0578\u056D\u0565\u056C \u0563\u0576\u0565\u0580\u0568'} ({selectedCourseIds.size} {'\u0564\u0561\u057D\u0568\u0576\u0569\u0561\u0581'})
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-primary">{'\u053D\u0574\u0562\u0561\u0575\u056B\u0576 \u0563\u056B\u0576 (\u0564\u0580\u0561\u0574/\u0561\u0574\u056B\u057D)'}</Label>
                        <Input
                          type="number"
                          min="0"
                          value={bulkGroupPrice}
                          onChange={(e) => setBulkGroupPrice(e.target.value)}
                          placeholder={'\u0546\u0578\u0580 \u0563\u056B\u0576 \u0576\u0577\u0565\u0584...'}
                          className="bg-muted border-border/70 mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-primary">{'\u0531\u0576\u0570\u0561\u057F\u0561\u056F\u0561\u0576 \u0563\u056B\u0576 (\u0564\u0580\u0561\u0574/\u0561\u0574\u056B\u057D)'}</Label>
                        <Input
                          type="number"
                          min="0"
                          value={bulkIndividualPrice}
                          onChange={(e) => setBulkIndividualPrice(e.target.value)}
                          placeholder={'\u0546\u0578\u0580 \u0563\u056B\u0576 \u0576\u0577\u0565\u0584...'}
                          className="bg-muted border-border/70 mt-1"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">{'\u0534\u0561\u057F\u0561\u0580\u056F \u0564\u0561\u0577\u057F\u0565\u0580\u0568 \u0569\u0578\u0572\u0565\u0584 \u0564\u0561\u057F\u0561\u0580\u056F \u0569\u0561\u0580\u0574\u0561\u0581\u0576\u0565\u056C\u0578\u0582 \u0570\u0561\u0574\u0561\u0580'}</p>
                      <div className="flex justify-end space-x-2">
                        <Button variant="outline" onClick={() => setIsBulkPriceDialogOpen(false)}>
                          {'\u0549\u0565\u0572\u0561\u0580\u056F\u0565\u056C'}
                        </Button>
                        <Button 
                          className="bg-primary hover:bg-primary/90"
                          onClick={handleBulkPriceSubmit}
                          disabled={bulkUpdatePricesMutation.isPending}
                        >
                          {bulkUpdatePricesMutation.isPending ? '\u0539\u0561\u0580\u0574\u0561\u0581\u057E\u0578\u0582\u0574 \u0567...' : '\u0539\u0561\u0580\u0574\u0561\u0581\u0576\u0565\u056C'}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                Ընտրեք ֆակուլտետ դասընթացները տեսնելու համար
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};